import './theme.css';
import type { LocalizedNavItem } from '@/app/lib/stage/nav-model.ts';
import type { AgentHeaderProps } from '@/app/lib/stage/StageHeader.tsx';
import type { FC } from 'react';
import { defineMessages, type MessageDescriptor } from 'react-intl';
import { StudioHeader } from './StudioHeader.tsx';

export interface SiteConfig {
  brandName: string;
  navItems: LocalizedNavItem[];
  defaultChips: MessageDescriptor[];
  /** Page-area mode: 'site' = the latest surface IS the page; 'chat' = a
   *  scrolling transcript with inline surfaces. Same shell either way. */
  mode: 'site' | 'chat';
  /** Site header (brand + menu). Off = minimal canvas: the rail keeps
   *  history, chips keep the standing intents, the dock keeps input.
   *  Template default is off; a branded site turns it on. */
  showHeader: boolean;
  /** Custom header component. Omit for the template's default header
   *  (monogram + name + menu); provide one for a bespoke composition —
   *  it receives resolved nav items + onNavigate, so menu mechanics and
   *  the agent's /nav overlay keep working. */
  Header?: FC<AgentHeaderProps>;
  /** First-load pending state shows the brand name (shimmer) instead of the
   *  neutral text skeleton — for agents with a website identity. */
  brandedArrival?: boolean;
  /** The site's designed look — a builder decision, like a real website's.
   *  'auto' follows the visitor's OS. */
  appearance: 'light' | 'dark' | 'auto';
  /** Omnibox placeholder override. */
  placeholder?: MessageDescriptor;
}

/** The landing panel carries the real subject choices and its own input, so the
 *  entry screen offers no chips underneath the green field — nothing the visitor
 *  needs sits below it. Later turns get their chips from the agent panel's own
 *  options, set by the working render. */
const chipMessages = defineMessages({
  composerPlaceholder: {
    id: 'sayMyName.composerPlaceholder',
    defaultMessage: 'I’m building…',
    description: 'Placeholder in the message composer, echoing the landing panel prompt',
  },
});

export const SITE_CONFIG: SiteConfig = {
  // Written exactly as the wordmark should read. The header renders it verbatim,
  // so the casing lives HERE and nowhere else — no CSS transform, no
  // toUpperCase() in the view.
  brandName: 'Say My Name',
  navItems: [],
  defaultChips: [],
  mode: 'site',
  showHeader: true,
  Header: StudioHeader,
  brandedArrival: true,
  appearance: 'dark',
  placeholder: chipMessages.composerPlaceholder,
};
